import { AgentProject } from '../agent-project/agent-project.js'
import { ADK_VERSION, formatCode } from './utils.js'
import { createFile } from '../utils/fs.js'
import path from 'path'

export async function generateConversationTypes(project: AgentProject): Promise<void> {
  // Map conversation definitions to their channel types
  const conversationTypes: Record<string, { channel: string | string[]; state: string }> = {}

  for (const conversationRef of project.conversations) {
    try {
      const conversationPath = path.join(project.path, conversationRef.path)
      // Bust module cache to ensure fresh conversation on type generation
      const conversationModule = await import(`${conversationPath}?t=${Date.now()}`)

      const conversationInstance = conversationModule[conversationRef.export] || conversationModule.default

      if (!conversationInstance) {
        continue
      }

      // Get the channel specification
      const channel = conversationInstance.channel

      // Extract state schema and convert to TypeScript type
      const stateType = conversationInstance.schema
        ? (conversationInstance.schema as any).toTypescriptType?.() || 'any'
        : '{}'

      // Determine the channel key(s)
      let channels: string[]
      if (channel === '*') {
        // Glob - we'll need to handle this specially
        channels = ['*']
      } else if (Array.isArray(channel)) {
        channels = channel as string[]
      } else {
        channels = [channel as string]
      }

      // Store conversation info for each channel
      for (const ch of channels) {
        conversationTypes[ch] = {
          channel: ch,
          state: stateType,
        }
      }
    } catch (error) {
      console.error(`Failed to process conversation ${conversationRef.export}:`, error)
    }
  }

  // Generate type definitions for each channel
  const channelDefinitions = Object.entries(conversationTypes)
    .filter(([channel]) => channel !== '*') // Filter out glob for now
    .map(([channel, info]) => {
      const [integration, channelName] = channel.split('.')
      return `    "${channel}": {
      channel: "${channel}";
      integration: "${integration}";
      state: ${info.state};
      tags: Integrations["${integration}"]["channels"]["${channelName}"]["conversation"]["tags"];
      messageTags: Integrations["${integration}"]["channels"]["${channelName}"]["message"]["tags"];
      messages: Integrations["${integration}"]["channels"]["${channelName}"]["messages"];
      events: Integrations["${integration}"]["events"];
    };`
    })
    .join('\n')

  const content = `
////////////////////////////////////////////////////////
// DO NOT EDIT THIS FILE DIRECTLY
// This file is auto-generated from the Botpress ADK
// ADK Version: ${ADK_VERSION}
// Generated at: ${new Date().toISOString()}
////////////////////////////////////////////////////////

type Integrations = import("@botpress/runtime/_types/integrations").Integrations;

declare module "@botpress/runtime/_types/conversations" {
  export type ConversationDefinitions = {
${channelDefinitions || '    // No conversations defined yet'}
  };
}
`

  // Write to conversation-types.d.ts in the .adk folder
  const conversationTypesPath = path.join(project.path, '.adk', 'conversation-types.d.ts')
  await createFile(conversationTypesPath, await formatCode(content))
}
